import { renderLienRouteur } from '@/components/ui/lien-routeur';
import { Card, Icon } from '@pms-digital/design-system';
import { NOYAU_CODES, type NoyauCode, type ResumeTraduction } from '@rosetta/shared';
import { formaterDate } from './formater-date';
import { MenuActionsCarte } from './menu-actions-carte';
import { PastillesNoyaux } from './pastilles-noyaux';

interface CarteTraductionProps {
  traduction: ResumeTraduction;
  /** The current instant, to word the date ("aujourd'hui"); injectable for the tests. */
  maintenant?: Date;
  /**
   * Opens the choice of the noyaux to add to this text (#11, N1), from the menu of the three
   * dots. Absent, or when the text is already translated towards the six noyaux, the card has no
   * menu.
   */
  onAutreNoyau?: () => void;
  /** The translation each noyau's pill opens (#36); absent, the pills are not links. */
  lancementsTraduits?: Readonly<Partial<Record<NoyauCode, string>>>;
}

/**
 * One translated text of the history (H8, #11): the `Card` of the design system (thin `gray-200`
 * frame). The title ("Sans titre" in grey italics when there is none) is a link to its result,
 * with the date under it, the first three lines of the original text and, at the bottom of the
 * body, the noyaux it is translated towards. The three dots at the top right open its menu,
 * "Traduire vers un autre noyau" (`menu-actions-carte.tsx`). The card as a whole opens the result
 * (#21) by the stretched link of the package (`titleHref`): the title stays the only link, and the
 * menu and the pills are above it. A translation still running has its own card (H6 and H9,
 * `carte-en-cours.tsx`). A partial one (#13: some noyaux did not succeed) is the same card, with
 * "traduite en partie" (`error`, `rouge-deep`) by the menu.
 */
export function CarteTraduction({
  traduction,
  maintenant,
  onAutreNoyau,
  lancementsTraduits,
}: CarteTraductionProps) {
  const { id, titre, texteOrigine, noyaux, creeLe, statut } = traduction;
  const nom = titre ?? 'Sans titre';
  const avecMenu = onAutreNoyau !== undefined && noyaux.length < NOYAU_CODES.length;
  return (
    <Card
      surface="border"
      label={`${nom}, traduction terminée`}
      title={
        titre === undefined ? <span className="font-normal text-gray-600 italic">{nom}</span> : nom
      }
      titleHref={`/resultat/${id}`}
      renderLink={renderLienRouteur}
      meta={<time dateTime={creeLe}>{formaterDate(creeLe, maintenant)}</time>}
      trailing={
        statut === 'partielle' || avecMenu ? (
          <span className="flex items-center gap-2">
            {statut === 'partielle' ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-caption text-rouge-deep">
                <Icon name="error" size={16} />
                traduite en partie
              </span>
            ) : null}
            {avecMenu ? <MenuActionsCarte nom={nom} onAutreNoyau={onAutreNoyau} /> : null}
          </span>
        ) : undefined
      }
    >
      <div className="flex h-full min-h-28 flex-col gap-3">
        <p className="m-0 line-clamp-3 font-light leading-[1.55]">{texteOrigine}</p>
        <div className="mt-auto flex flex-wrap items-center gap-2">
          <PastillesNoyaux
            noyaux={noyaux}
            {...(lancementsTraduits === undefined ? {} : { traductions: lancementsTraduits })}
          />
        </div>
      </div>
    </Card>
  );
}
